/**
 * @fileoverview Types for Ramp integration
 */

import * as yup from 'yup'

// AUTH --------------------------------------------------------------------------------------------
export enum RampAuthScopes {
  BillsRead = 'bills:read',
  BillsWrite = 'bills:write',
  POsRead = 'purchase_orders:read',
  POsWrite = 'purchase_orders:write',
  ItemReceiptsRead = 'item_receipts:read',
  ItemReceiptsWrite = 'item_receipts:write',
  VendorsRead = 'vendors:read',
  VendorsWrite = 'vendors:write',
  ReceiptsRead = 'receipts:read',
  AccountingRead = 'accounting:read',
  TransfersRead = 'transfers:read',
  AccountingWrite = 'accounting:write',
}

export const RampAuthScopesToHumanReadableMap: { [key: string]: string } = {
  [RampAuthScopes.BillsRead]: 'Read Bills',
  [RampAuthScopes.BillsWrite]: 'Write Bills',
  [RampAuthScopes.POsRead]: 'Read Purchase Orders',
  [RampAuthScopes.POsWrite]: 'Write Purchase Orders',
  [RampAuthScopes.ItemReceiptsRead]: 'Read Item Receipts',
  [RampAuthScopes.ItemReceiptsWrite]: 'Write Item Receipts',
  [RampAuthScopes.ReceiptsRead]: 'Read Receipts',
  [RampAuthScopes.AccountingRead]: 'Read Accounting Data',
  [RampAuthScopes.AccountingWrite]: 'Write Accounting Data',
  [RampAuthScopes.TransfersRead]: 'Read Transfers',
  [RampAuthScopes.VendorsRead]: 'Read Vendors',
  [RampAuthScopes.VendorsWrite]: 'Write Vendors',
}

// SHARED SCHEMAS -----------------------------------------------------------------------------------------

/** Accounting Field Type Enum */
export enum RampAccountingFieldType {
  BILLABLE = 'BILLABLE',
  BOOLEAN = 'BOOLEAN',
  CLASS = 'CLASS',
  CUSTOMER = 'CUSTOMER',
  DEPARTMENT = 'DEPARTMENT',
  EMPLOYEE = 'EMPLOYEE',
  ITEM = 'ITEM',
  LOCATION = 'LOCATION',
  NUMBER = 'NUMBER',
  PAYMENT_METHOD = 'PAYMENT_METHOD',
  PROJECT = 'PROJECT',
  SELECT = 'SELECT',
  SUB_CUSTOMER = 'SUB_CUSTOMER',
  TEXT = 'TEXT',
  VENDOR = 'VENDOR',
}

/** Money Amount Schema */
export const rampMoneyAmountSchema = yup.object({
  amount: yup.number().nullable().optional(),
  currency_code: yup.string().nullable().optional(),
  minor_unit_conversion_rate: yup.number().integer().nullable().optional(),
})

export type RampMoneyAmount = yup.InferType<typeof rampMoneyAmountSchema>

/** Accounting Field Category Info Schema */
export const rampAccountingFieldCategoryInfoSchema = yup.object({
  external_id: yup.string().nullable().optional(),
  id: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  type: yup.string().oneOf(Object.values(RampAccountingFieldType)).nullable().optional(),
})

export type RampAccountingFieldCategoryInfo = yup.InferType<
  typeof rampAccountingFieldCategoryInfoSchema
>

/** Accounting Field Selection Schema */
export const rampAccountingFieldSelectionSchema = yup.object({
  category_info: rampAccountingFieldCategoryInfoSchema.nullable().optional(),
  field_external_id: yup.string().nullable().optional(),
  field_name: yup.string().nullable().optional(),
  field_option_external_id: yup.string().nullable().optional(),
  field_option_name: yup.string().nullable().optional(),
  external_id: yup.string().nullable().optional(),
  id: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  external_code: yup.string().nullable().optional(),
})

export type RampAccountingFieldSelection = yup.InferType<typeof rampAccountingFieldSelectionSchema>

// ITEM RECEIPTS -----------------------------------------------------------------------------------------

/** Item Receipt Line Item Schema */
export const rampItemReceiptLineItemSchema = yup.object({
  id: yup.string().nullable().optional(),
  purchase_order_line_item_id: yup.string().nullable().optional(),
  quantity: yup.number().nullable().optional(),
  unit_price: rampMoneyAmountSchema.nullable().optional(),
  amount: rampMoneyAmountSchema.nullable().optional(),
  memo: yup.string().nullable().optional(),
  accounting_field_selections: yup
    .array()
    .of(rampAccountingFieldSelectionSchema)
    .nullable()
    .optional(),
})

export type RampItemReceiptLineItem = yup.InferType<typeof rampItemReceiptLineItemSchema>

/** Item Receipt Schema */
export const rampItemReceiptSchema = yup.object({
  id: yup.string().required(),
  item_receipt_number: yup.string().nullable().optional(),
  purchase_order_id: yup.string().nullable().optional(),
  received_at: yup.string().nullable().optional(),
  memo: yup.string().nullable().optional(),
  remote_id: yup.string().nullable().optional(),
  line_items: yup.array().of(rampItemReceiptLineItemSchema).nullable().optional(),
  documents: yup.array().of(yup.string()).nullable().optional(),
  archived_at: yup.string().nullable().optional(),
  created_at: yup.string().nullable().optional(),
})

export type RampItemReceipt = yup.InferType<typeof rampItemReceiptSchema>

/** Item Receipt Fetch Response Schema */
export const rampItemReceiptFetchResponseSchema = yup.object({
  data: rampItemReceiptSchema.required(),
})

export type RampItemReceiptFetchResponse = yup.InferType<typeof rampItemReceiptFetchResponseSchema>

/** Item Receipt List Response Schema */
export const rampItemReceiptListResponseSchema = yup.object({
  data: yup.array().of(rampItemReceiptSchema).required(),
  page: yup
    .object({
      next: yup.string().nullable().optional(),
    })
    .optional(),
})

export type RampItemReceiptListResponse = yup.InferType<typeof rampItemReceiptListResponseSchema>

/** Item Receipt Line Item Input Schema */
export const rampItemReceiptLineItemInputSchema = yup.object({
  purchase_order_line_item_id: yup.string().required(),
  unit_quantity: yup.number().integer().nullable().optional(),
})

export type RampItemReceiptLineItemInput = yup.InferType<typeof rampItemReceiptLineItemInputSchema>

/** Item Receipt Create Input Schema */
export const rampItemReceiptCreateInputSchema = yup.object({
  item_receipt_line_items: yup.array().of(rampItemReceiptLineItemInputSchema).min(1).required(),
  item_receipt_number: yup.string().required(),
  purchase_order_id: yup.string().required(),
  received_at: yup.string().required(),
  memo: yup.string().nullable().optional(),
})

export type RampItemReceiptCreateInput = yup.InferType<typeof rampItemReceiptCreateInputSchema>

/* Item Receipt Create Response Schema */
export const rampItemReceiptCreateResponseSchema = rampItemReceiptSchema

export type RampItemReceiptCreateResponse = yup.InferType<
  typeof rampItemReceiptCreateResponseSchema
>

// BILLS -----------------------------------------------------------------------------------------

/** Bill Payment Method Enum */
export enum RampBillPaymentMethod {
  ACH = 'ACH',
  AUTOMATIC_CARD_PAYMENT = 'AUTOMATIC_CARD_PAYMENT',
  CARD = 'CARD',
  CHECK = 'CHECK',
  CRYPTO_WALLET_TRANSFER = 'CRYPTO_WALLET_TRANSFER',
  DOMESTIC_WIRE = 'DOMESTIC_WIRE',
  INTERNATIONAL = 'INTERNATIONAL',
  LOCAL_BANK_TRANSFER = 'LOCAL_BANK_TRANSFER',
  ONE_TIME_CARD = 'ONE_TIME_CARD',
  ONE_TIME_CARD_DELIVERY = 'ONE_TIME_CARD_DELIVERY',
  PAID_MANUALLY = 'PAID_MANUALLY',
  RTP = 'RTP',
  SWIFT = 'SWIFT',
  UNSPECIFIED = 'UNSPECIFIED',
  VENDOR_CREDIT = 'VENDOR_CREDIT',
}

/** Bill Accounting Field Selection Input Schema */
export const rampBillAccountingFieldSelectionInputSchema = yup.object({
  field_external_id: yup.string().required(),
  field_option_external_id: yup.string().nullable().optional(),
  free_form_text: yup.string().nullable().optional(),
})

export type RampBillAccountingFieldSelectionInput = yup.InferType<
  typeof rampBillAccountingFieldSelectionInputSchema
>

/** Bill Inventory Line Item Input Schema */
export const rampBillInventoryLineItemInputSchema = yup.object({
  unit_price: yup.mixed().oneOf([yup.string(), yup.number()]).required(),
  unit_quantity: yup.number().integer().required(),
  accounting_field_selections: yup
    .array()
    .of(rampBillAccountingFieldSelectionInputSchema)
    .optional(),
  memo: yup.string().nullable().optional(),
})

export type RampBillInventoryLineItemInput = yup.InferType<
  typeof rampBillInventoryLineItemInputSchema
>

/** Bill Line Item Input Schema */
export const rampBillLineItemInputSchema = yup.object({
  amount: yup.mixed().oneOf([yup.string(), yup.number()]).required(),
  accounting_field_selections: yup
    .array()
    .of(rampBillAccountingFieldSelectionInputSchema)
    .optional(),
  memo: yup.string().nullable().optional(),
})

export type RampBillLineItemInput = yup.InferType<typeof rampBillLineItemInputSchema>

/** Bill Payment Details Schema */
export const rampBillPaymentDetailsSchema = yup.object({
  payment_arrival_date: yup.string().required(),
  source_bank_account_id: yup.string().required(),
  vendor_account_id: yup.string().required(),
  is_same_day: yup.boolean().optional(),
})

export type RampBillPaymentDetails = yup.InferType<typeof rampBillPaymentDetailsSchema>

/** Bill Create Input Schema */
export const rampBillCreateInputSchema = yup.object({
  vendor_id: yup.string().required(),
  invoice_number: yup.string().max(20).required(),
  issued_at: yup.string().required(),
  due_at: yup.string().optional(),
  entity_id: yup.string().optional(),
  invoice_currency: yup.string().optional(),
  payment_method: yup.string().oneOf(Object.values(RampBillPaymentMethod)).optional(),
  vendor_contact_id: yup.string().optional(),
  accounting_field_selections: yup
    .array()
    .of(rampBillAccountingFieldSelectionInputSchema)
    .optional(),
  enable_accounting_sync: yup.boolean().optional(),
  inventory_line_items: yup.array().of(rampBillInventoryLineItemInputSchema).optional(),
  line_items: yup.array().of(rampBillLineItemInputSchema).optional(),
  memo: yup.string().max(1000).nullable().optional(),
  payment_details: rampBillPaymentDetailsSchema.nullable().optional(),
  posting_date: yup.string().nullable().optional(),
  remote_id: yup.string().nullable().optional(),
})

export type RampBillCreateInput = yup.InferType<typeof rampBillCreateInputSchema>

/** Bill Owner Schema */
export const rampBillOwnerSchema = yup.object({
  id: yup.string().required(),
  first_name: yup.string().nullable().optional(),
  last_name: yup.string().nullable().optional(),
})

export type RampBillOwner = yup.InferType<typeof rampBillOwnerSchema>

/** Bill Vendor Schema */
export const rampBillVendorSchema = yup.object({
  id: yup.string().optional(),
  name: yup.string().optional(),
  remote_code: yup.string().optional(),
  remote_id: yup.string().optional(),
  remote_name: yup.string().optional(),
  type: yup.string().optional(),
})

export type RampBillVendor = yup.InferType<typeof rampBillVendorSchema>

/** Bill Payment Schema */
export const rampBillPaymentSchema = yup.object({
  amount: rampMoneyAmountSchema.required(),
  effective_date: yup.string().optional(),
  payment_date: yup.string().optional(),
  payment_method: yup.string().optional(),
  trace_id: yup
    .object({
      descriptor: yup.string().optional(),
      trace_id: yup.string().optional(),
    })
    .optional(),
})

export type RampBillPayment = yup.InferType<typeof rampBillPaymentSchema>

/** Bill Accounting Field Selection Output Schema */
export const rampBillAccountingFieldSelectionOutputSchema = yup.object({
  category_info: rampAccountingFieldCategoryInfoSchema.nullable().optional(),
  external_id: yup.string().nullable().optional(),
  id: yup.string().nullable().optional(),
  name: yup.string().nullable().optional(),
  type: yup.string().nullable().optional(),
  provider_name: yup.string().nullable().optional(),
  external_code: yup.string().nullable().optional(),
})

export type RampBillAccountingFieldSelectionOutput = yup.InferType<
  typeof rampBillAccountingFieldSelectionOutputSchema
>

/** Bill Inventory Line Item Output Schema */
export const rampBillInventoryLineItemOutputSchema = yup.object({
  accounting_field_selections: yup
    .array()
    .of(rampBillAccountingFieldSelectionOutputSchema)
    .optional(),
  amount: rampMoneyAmountSchema.optional(),
  memo: yup.string().nullable().optional(),
  unit_price: rampMoneyAmountSchema.optional(),
  unit_quantity: yup.number().nullable().optional(),
})

export type RampBillInventoryLineItemOutput = yup.InferType<
  typeof rampBillInventoryLineItemOutputSchema
>

/** Bill Line Item Output Schema */
export const rampBillLineItemOutputSchema = yup.object({
  accounting_field_selections: yup
    .array()
    .of(rampBillAccountingFieldSelectionOutputSchema)
    .optional(),
  amount: rampMoneyAmountSchema.optional(),
  memo: yup.string().nullable().optional(),
})

export type RampBillLineItemOutput = yup.InferType<typeof rampBillLineItemOutputSchema>

/** Bill Schema (Response) */
export const rampBillSchema = yup.object({
  id: yup.string().required(),
  accounting_date: yup.string().nullable().optional(),
  accounting_field_selections: yup
    .array()
    .of(rampBillAccountingFieldSelectionOutputSchema)
    .optional(),
  accounting_sync_date: yup.string().nullable().optional(),
  amount: rampMoneyAmountSchema.optional(),
  approval_status: yup.string().nullable().optional(),
  archived_at: yup.string().nullable().optional(),
  bill_owner: rampBillOwnerSchema.nullable().optional(),
  created_at: yup.string().nullable().optional(),
  deep_link_url: yup.string().nullable().optional(),
  due_at: yup.string().nullable().optional(),
  enable_accounting_sync: yup.boolean().nullable().optional(),
  entity_id: yup.string().nullable().optional(),
  fx_conversion_rate: yup.string().nullable().optional(),
  inventory_line_items: yup.array().of(rampBillInventoryLineItemOutputSchema).optional(),
  invoice_number: yup.string().optional(),
  invoice_urls: yup.array().of(yup.string()).optional(),
  issued_at: yup.string().nullable().optional(),
  line_items: yup.array().of(rampBillLineItemOutputSchema).optional(),
  memo: yup.string().nullable().optional(),
  paid_at: yup.string().nullable().optional(),
  payment: rampBillPaymentSchema.nullable().optional(),
  posting_date: yup.string().nullable().optional(),
  remote_id: yup.string().nullable().optional(),
  status: yup.string().nullable().optional(),
  sync_status: yup.string().nullable().optional(),
  vendor: rampBillVendorSchema.nullable().optional(),
  vendor_contact_id: yup.string().nullable().optional(),
  vendor_memo: yup.string().nullable().optional(),
})

export type RampBill = yup.InferType<typeof rampBillSchema>

/** Bill Create Response Schema */
export const rampBillCreateResponseSchema = yup.object({
  data: rampBillSchema.required(),
})

export type RampBillCreateResponse = yup.InferType<typeof rampBillCreateResponseSchema>

/** Bill Update Input Schema - all fields optional except bill_id */
export const rampBillUpdateInputSchema = yup.object({
  bill_id: yup.string().required(),
  vendor_id: yup.string().optional(),
  invoice_number: yup.string().max(20).optional(),
  issued_at: yup.string().optional(),
  due_at: yup.string().optional(),
  entity_id: yup.string().optional(),
  payment_method: yup.string().oneOf(Object.values(RampBillPaymentMethod)).optional(),
  vendor_contact_id: yup.string().optional(),
  accounting_field_selections: yup
    .array()
    .of(rampBillAccountingFieldSelectionInputSchema)
    .optional(),
  inventory_line_items: yup.array().of(rampBillInventoryLineItemInputSchema).optional(),
  line_items: yup.array().of(rampBillLineItemInputSchema).optional(),
  memo: yup.string().max(1000).nullable().optional(),
  posting_date: yup.string().nullable().optional(),
  purchase_order_ids: yup.array().of(yup.string().required()).optional(),
  remote_id: yup.string().nullable().optional(),
})

export type RampBillUpdateInput = yup.InferType<typeof rampBillUpdateInputSchema>

/** Bill Update Response Schema */
export const rampBillUpdateResponseSchema = yup.object({
  data: rampBillSchema.required(),
})

export type RampBillUpdateResponse = yup.InferType<typeof rampBillUpdateResponseSchema>

// PURCHASE ORDERS -----------------------------------------------------------------------------------------

/** Purchase Order Billing Status Enum */
export enum RampPurchaseOrderBillingStatus {
  CLOSED = 'CLOSED',
  DELETED = 'DELETED',
  FULLY_BILLED = 'FULLY_BILLED',
  OPEN = 'OPEN',
  PARTIALLY_BILLED = 'PARTIALLY_BILLED',
}

/** Purchase Order Receipt Status Enum */
export enum RampPurchaseOrderReceiptStatus {
  FULLY_RECEIVED = 'FULLY_RECEIVED',
  NOT_RECEIVED = 'NOT_RECEIVED',
  OVER_RECEIVED = 'OVER_RECEIVED',
  PARTIALLY_RECEIVED = 'PARTIALLY_RECEIVED',
}

/** Purchase Order Line Item Schema */
export const rampPurchaseOrderLineItemSchema = yup.object({
  accounting_category_id: yup.string().nullable().optional(),
  accounting_category_name: yup.string().nullable().optional(),
  accounting_field_selections: yup
    .array()
    .of(rampAccountingFieldSelectionSchema)
    .nullable()
    .optional(),
  amount: rampMoneyAmountSchema.nullable().optional(),
  description: yup.string().nullable().optional(),
  expected_amount: rampMoneyAmountSchema.nullable().optional(),
  id: yup.string().required(),
  item_id: yup.string().nullable().optional(),
  item_name: yup.string().nullable().optional(),
  quantity: yup.number().nullable().optional(),
  unit_price: rampMoneyAmountSchema.nullable().optional(),
})

export type RampPurchaseOrderLineItem = yup.InferType<typeof rampPurchaseOrderLineItemSchema>

/** Purchase Order Schema */
export const rampPurchaseOrderSchema = yup.object({
  accounting_field_selections: yup
    .array()
    .of(rampAccountingFieldSelectionSchema)
    .nullable()
    .optional(),
  billing_status: yup
    .string()
    .oneOf(Object.values(RampPurchaseOrderBillingStatus))
    .nullable()
    .optional(),
  company_entity_id: yup.string().nullable().optional(),
  creation_source: yup.string().nullable().optional(),
  created_at: yup.string().nullable().optional(),
  currency: yup.string().nullable().optional(),
  deleted_at: yup.string().nullable().optional(),
  expected_delivery_date: yup.string().nullable().optional(),
  id: yup.string().required(),
  line_items: yup.array().of(rampPurchaseOrderLineItemSchema).nullable().optional(),
  memo: yup.string().nullable().optional(),
  order_date: yup.string().nullable().optional(),
  purchase_order_number: yup.string().nullable().optional(),
  receipt_status: yup
    .string()
    .oneOf(Object.values(RampPurchaseOrderReceiptStatus))
    .nullable()
    .optional(),
  shipping_address_id: yup.string().nullable().optional(),
  spend_request_id: yup.string().nullable().optional(),
  status: yup.string().nullable().optional(),
  subsidiary_id: yup.string().nullable().optional(),
  three_way_match_enabled: yup.boolean().nullable().optional(),
  total_amount: rampMoneyAmountSchema.nullable().optional(),
  updated_at: yup.string().nullable().optional(),
  vendor_id: yup.string().nullable().optional(),
  vendor_name: yup.string().nullable().optional(),
})

export type RampPurchaseOrder = yup.InferType<typeof rampPurchaseOrderSchema>

/** Purchase Order Fetch Response Schema */
export const rampPurchaseOrderFetchResponseSchema = yup.object({
  data: rampPurchaseOrderSchema.required(),
})

export type RampPurchaseOrderFetchResponse = yup.InferType<
  typeof rampPurchaseOrderFetchResponseSchema
>

/** Purchase Order List Response Schema */
export const rampPurchaseOrderListResponseSchema = yup.object({
  data: yup.array().of(rampPurchaseOrderSchema).required(),
  page: yup
    .object({
      next: yup.string().nullable().optional(),
    })
    .optional(),
})

export type RampPurchaseOrderListResponse = yup.InferType<
  typeof rampPurchaseOrderListResponseSchema
>

// VENDORS -----------------------------------------------------------------------------------------

/** Vendor Address Schema */
export const rampVendorAddressSchema = yup.object({
  address_line_1: yup.string().nullable().optional(),
  address_line_2: yup.string().nullable().optional(),
  city: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  postal_code: yup.string().nullable().optional(),
  country: yup.string().nullable().optional(),
})

export type RampVendorAddress = yup.InferType<typeof rampVendorAddressSchema>

/** Vendor Default Payment Method Schema */
export const rampVendorDefaultPaymentMethodSchema = yup.object({
  policy: yup
    .object({
      data: yup
        .object({
          account_nickname: yup.string().nullable().optional(),
          currency: yup.string().nullable().optional(),
          id: yup.string().nullable().optional(),
          is_default: yup.boolean().nullable().optional(),
          payment_method: yup.string().nullable().optional(),
        })
        .nullable()
        .optional(),
      kind: yup.string().nullable().optional(),
    })
    .nullable()
    .optional(),
  update_source: yup.string().nullable().optional(),
})

export type RampVendorDefaultPaymentMethod = yup.InferType<
  typeof rampVendorDefaultPaymentMethodSchema
>

/** Vendor SK Category Enum (Ramp category codes) */
export enum RampVendorSkCategory {
  PET = 1,
  OTHER = 2,
  OFFICE = 3,
  AIRLINES = 4,
  CAR_RENTAL = 5,
  LODGING = 6,
  TRAVEL_MISCELLANEOUS = 7,
  TAXI_AND_RIDESHARE = 8,
  FREIGHT_MOVING_AND_DELIVERY_SERVICES = 9,
  SHIPPING = 10,
  UTILITIES = 11,
  OFFICE_SUPPLIES_AND_CLEANING = 12,
  GENERAL_MERCHANDISE = 13,
  ELECTRONICS = 14,
  CLOTHING = 15,
  BOOKS_AND_NEWSPAPERS = 16,
  SUPERMARKETS_AND_GROCERY_STORES = 17,
  FUEL_AND_GAS = 18,
  RESTAURANTS = 19,
  ALCOHOL_AND_BARS = 20,
  MEDICAL = 21,
  // 22 is not a valid value
  FEES_AND_FINANCIAL_INSTITUTIONS = 23,
  ENTERTAINMENT = 24,
  PROFESSIONAL_SERVICES = 25,
  TAXES_AND_TAX_PREPARATION = 26,
  ADVERTISING = 27,
  PARKING = 28,
  CAR_SERVICES = 29,
  GAMBLING = 30,
  CLUBS_AND_MEMBERSHIPS = 31,
  LEGAL = 32,
  EDUCATION = 33,
  CHARITABLE_DONATIONS = 34,
  POLITICAL_ORGANIZATIONS = 35,
  RELIGIOUS_ORGANIZATIONS = 36,
  FINES = 37,
  GOVERNMENT_SERVICES = 38,
  INTRA_COMPANY_PURCHASES = 39,
  SAAS_SOFTWARE = 40,
  CLOUD_COMPUTING = 41,
  STREAMING_SERVICES = 42,
  INTERNET_AND_PHONE = 43,
  INSURANCE = 44,
}

/** Vendor Billing Frequency Enum */
export enum RampVendorBillingFrequency {
  ANNUAL = 'ANNUAL',
  MONTHLY = 'MONTHLY',
  MULTIPLE = 'MULTIPLE',
  NA = 'NA',
  OTHER = 'OTHER',
  QUARTERLY = 'QUARTERLY',
  ROLLING = 'ROLLING',
  TBD = 'TBD',
  TWICE_A_YEAR = 'TWICE_A_YEAR',
}

/** Vendor Federal Tax Classification Enum */
export enum RampVendorFederalTaxClassification {
  C_CORPORATION = 'C_CORPORATION',
  INDIVIDUAL_SOLE_PROPRIETOR_SINGLE_MEMBER_LLC = 'INDIVIDUAL_SOLE_PROPRIETOR_SINGLE_MEMBER_LLC',
  INTERNATIONAL = 'INTERNATIONAL',
  LLC_C_CORPORATION = 'LLC_C_CORPORATION',
  LLC_PARTNERSHIP = 'LLC_PARTNERSHIP',
  LLC_S_CORPORATION = 'LLC_S_CORPORATION',
  OTHER = 'OTHER',
  PARTNERSHIP = 'PARTNERSHIP',
  S_CORPORATION = 'S_CORPORATION',
  TRUST_ESTATE = 'TRUST_ESTATE',
}

/** Vendor Type Enum */
export enum RampVendorType {
  BUSINESS = 'BUSINESS',
  INDIVIDUAL = 'INDIVIDUAL',
}

/** Vendor Schema */
export const rampVendorSchema = yup.object({
  id: yup.string().required(),
  name: yup.string().nullable().optional(),
  name_legal: yup.string().nullable().optional(),
  accounting_vendor_remote_id: yup.string().nullable().optional(),
  external_vendor_id: yup.string().nullable().optional(),
  default_entity_id: yup.string().nullable().optional(),
  vendor_owner_id: yup.string().nullable().optional(),
  vendor_type: yup.string().oneOf(Object.values(RampVendorType)).nullable().optional(),
  is_active: yup.boolean().nullable().optional(),
  is_deletable: yup.boolean().nullable().optional(),
  merchant_id: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  federal_tax_classification: yup
    .string()
    .oneOf(Object.values(RampVendorFederalTaxClassification))
    .nullable()
    .optional(),
  country: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  sk_category_id: yup
    .number()
    .integer()
    .oneOf(Object.values(RampVendorSkCategory).filter((v): v is number => typeof v === 'number'))
    .nullable()
    .optional(),
  sk_category_name: yup.string().nullable().optional(),
  billing_frequency: yup
    .string()
    .oneOf(Object.values(RampVendorBillingFrequency))
    .nullable()
    .optional(),
  created_at: yup.string().nullable().optional(),
  address: rampVendorAddressSchema.nullable().optional(),
  tax_address: rampVendorAddressSchema.nullable().optional(),
  default_payment_method: rampVendorDefaultPaymentMethodSchema.nullable().optional(),
  contacts: yup.array().of(yup.string()).nullable().optional(),
  total_spend_all_time: rampMoneyAmountSchema.nullable().optional(),
  total_spend_last_30_days: rampMoneyAmountSchema.nullable().optional(),
  total_spend_last_365_days: rampMoneyAmountSchema.nullable().optional(),
  total_spend_ytd: rampMoneyAmountSchema.nullable().optional(),
})

export type RampVendor = yup.InferType<typeof rampVendorSchema>

/** Vendor List Response Schema */
export const rampVendorListResponseSchema = yup.object({
  data: yup.array().of(rampVendorSchema).required(),
  page: yup
    .object({
      next: yup.string().nullable().optional(),
    })
    .optional(),
})

export type RampVendorListResponse = yup.InferType<typeof rampVendorListResponseSchema>

/** Vendor Contact Input Schema (for create/update requests) */
export const rampVendorContactInputSchema = yup.object({
  email: yup.string().required(),
  first_name: yup.string().nullable().optional(),
  last_name: yup.string().nullable().optional(),
  phone: yup.string().nullable().optional(),
})

export type RampVendorContactInput = yup.InferType<typeof rampVendorContactInputSchema>

/** Vendor Create Input Schema */
export const rampVendorCreateInputSchema = yup.object({
  name: yup.string().min(1).nullable().optional(),
  country: yup.string().nullable().optional(),
  state: yup.string().nullable().optional(),
  vendor_owner_id: yup.string().nullable().optional(),
  accounting_vendor_remote_id: yup.string().nullable().optional(),
  vendor_tracking_category_option_id: yup.string().nullable().optional(),
  external_vendor_id: yup.string().nullable().optional(),
  request_payment_details: yup.boolean().nullable().optional(),
  request_tax_details: yup.boolean().nullable().optional(),
  business_vendor_contacts: rampVendorContactInputSchema.nullable().optional(),
  address: rampVendorAddressSchema.nullable().optional(),
})

export type RampVendorCreateInput = yup.InferType<typeof rampVendorCreateInputSchema>

/** Vendor Create Response Schema */
export const rampVendorCreateResponseSchema = rampVendorSchema

export type RampVendorCreateResponse = yup.InferType<typeof rampVendorCreateResponseSchema>

/** Vendor Update Input Schema */
export const rampVendorUpdateInputSchema = yup.object({
  vendor_id: yup.string().required(),
  accounting_vendor_remote_id: yup.string().nullable().optional(),
  vendor_tracking_category_option_id: yup.string().nullable().optional(),
  address: rampVendorAddressSchema.nullable().optional(),
  country: yup.string().nullable().optional(),
  description: yup.string().nullable().optional(),
  external_vendor_id: yup.string().nullable().optional(),
  is_active: yup.boolean().nullable().optional(),
  state: yup.string().nullable().optional(),
})

export type RampVendorUpdateInput = yup.InferType<typeof rampVendorUpdateInputSchema>

/** Vendor Update Response Schema */
export const rampVendorUpdateResponseSchema = rampVendorSchema

export type RampVendorUpdateResponse = yup.InferType<typeof rampVendorUpdateResponseSchema>

// RECEIPTS -----------------------------------------------------------------------------------------

/** Receipt Schema */
export const rampReceiptSchema = yup.object({
  id: yup.string().required(),
  created_at: yup.string().nullable().optional(),
  receipt_url: yup.string().nullable().optional(),
  transaction_id: yup.string().nullable().optional(),
  user_id: yup.string().nullable().optional(),
})

export type RampReceipt = yup.InferType<typeof rampReceiptSchema>

/** Receipt List Response Schema */
export const rampReceiptListResponseSchema = yup.object({
  data: yup.array().of(rampReceiptSchema).required(),
  page: yup
    .object({
      next: yup.string().nullable().optional(),
    })
    .optional(),
})

export type RampReceiptListResponse = yup.InferType<typeof rampReceiptListResponseSchema>

// GL ACCOUNTS -----------------------------------------------------------------------------------------

/** GL Account Category Info Schema (API may return id/name or remote_id/remote_name) */
const rampGLAccountCategoryInfoSchema = yup.object({
  id: yup.string().notRequired(),
  name: yup.string().notRequired(),
  ramp_id: yup.string().notRequired(),
  remote_id: yup.string().notRequired(),
  remote_name: yup.string().notRequired(),
})

/** GL Account Schema */
export const rampGLAccountSchema = yup.object({
  accounting_connection_id: yup.string().notRequired(),
  created_at: yup.string().notRequired(),
  is_active: yup.boolean().required(),
  name: yup.string().required(),
  provider_name: yup.string().nullable().notRequired(),
  ramp_id: yup.string().required(),
  classification: yup
    .string()
    .nullable()
    .oneOf([
      'ANY',
      'ASSET',
      'CREDCARD',
      'EQUITY',
      'EXPENSE',
      'LIABILITY',
      'REVENUE',
      'UNKNOWN',
      null,
    ])
    .notRequired(),
  code: yup.string().notRequired(),
  gl_account_category_info: rampGLAccountCategoryInfoSchema.notRequired(),
  id: yup.string().notRequired(),
  updated_at: yup.string().notRequired(),
})

export type RampGLAccount = yup.InferType<typeof rampGLAccountSchema>

/** Upload GL Accounts Response Schema */
export const rampUploadGLAccountsResponseSchema = yup.object({
  accounting_connection_id: yup.string().notRequired(),
  gl_accounts: yup.array(rampGLAccountSchema).notRequired(),
  uploaded: yup.array().of(yup.string().required()).notRequired(),
})

export type RampUploadGLAccountsResponse = yup.InferType<typeof rampUploadGLAccountsResponseSchema>

/** Update GL Account Response Schema */
export const rampUpdateGLAccountResponseSchema = rampGLAccountSchema
export type RampUpdateGLAccountResponse = yup.InferType<typeof rampUpdateGLAccountResponseSchema>
